import { useMemo } from 'react';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { type CountryData, type CountryIso2, defaultCountries, parseCountry } from 'react-international-phone';

import type { AppConfig } from '@/types/config';
import { normalizeAllowedPhonePrefixes } from '@/lib/phone-prefix';

export type PhoneInputConfig = {
    countries: CountryData[];
    defaultCountry: CountryIso2;
    allowedPhonePrefixes: string[];
};

/**
 * Adapts the phone-input catalog to an explicit prefix list.
 * An omitted or empty list leaves every country selectable (address and the other free-prefix forms).
 * Match is equality on the dial code: `+39` does not include another code that merely starts with 39.
 */
export function useAllowedPhoneCountries(allowedPrefixes?: string | readonly string[]): PhoneInputConfig {
    const config = useConfig<AppConfig>();
    const prefixKey = normalizeAllowedPhonePrefixes(allowedPrefixes).join('\u0000');

    return useMemo(() => {
        const prefixes = prefixKey ? prefixKey.split('\u0000').filter(Boolean) : [];
        const countries =
            prefixes.length === 0
                ? defaultCountries
                : defaultCountries.filter((country) => prefixes.includes(`+${parseCountry(country).dialCode}`));
        const preferred = (config.phoneInput?.defaultCountry ?? 'it').toLowerCase();
        const preferredCountry = countries.find((country) => parseCountry(country).iso2 === preferred);
        const fallbackCountry = countries[0] ? parseCountry(countries[0]).iso2 : 'it';

        return {
            countries,
            defaultCountry: (preferredCountry ? preferred : fallbackCountry) as CountryIso2,
            allowedPhonePrefixes: prefixes,
        };
    }, [config.phoneInput?.defaultCountry, prefixKey]);
}

/** Unfiltered phone input. Registration and profile pass `useAllowedPhoneCountries` a prefix list instead. */
export function usePhoneInputConfig(): PhoneInputConfig {
    return useAllowedPhoneCountries();
}
